import { areAllSourcesInDeviceMode } from '@components/destinations/destination-view/utils';
import { ActionType, ResourceType, useActionGuard } from '@hooks/useActionGuard';
import { constructPermissionResult } from '@hooks/useActionGuard/utils';
import { useLockedResource } from '@hooks/useLockedResource';
import { LockedResourceType } from '@api';
import { getMessageList } from '@lib/utils/getMessageList';
import { IDestinationStore } from '@stores/destination';
import useStores from '@stores/useStores';

export const canCreateTransformation = () => {
  const { canPerformAction } = useActionGuard();

  return canPerformAction({
    old: { resourceType: 'transformation', action: 'create' },
    new: {
      actionType: ActionType.CREATE,
      resource: {
        type: ResourceType.TRANSFORMATION,
      },
    },
  });
};

export const canEditTransformation = (id: string) => {
  const { canPerformAction } = useActionGuard();

  return canPerformAction({
    old: { resourceType: 'transformation', action: 'edit' },
    new: {
      actionType: ActionType.EDIT,
      resource: {
        type: ResourceType.TRANSFORMATION,
        id,
      },
    },
  });
};

export const canDeleteTransformation = (id: string) => {
  const { canPerformAction } = useActionGuard();
  const { transformationsListStore } = useStores();
  const transformation = transformationsListStore.getById(id);

  if (!transformation) {
    return constructPermissionResult(false, 'Transformation not found');
  }

  const permission = canPerformAction({
    old: { resourceType: 'transformation', action: 'edit' },
    new: {
      actionType: ActionType.DELETE,
      resource: {
        type: ResourceType.TRANSFORMATION,
        id,
      },
    },
  });

  return {
    isAllowed: permission.isAllowed && transformation.destinations.length === 0,
    denialReason: getMessageList([
      transformation.destinations.length > 0
        ? 'You cannot delete a transformation connected to destinations'
        : '',
      permission.denialReason,
    ]),
  };
};

export const canConnectTransformationToExistingDestinations = (transformationId: string) => {
  const { canPerformAction, isNewPermissionModelEnforcementEnabled } = useActionGuard();
  const { destinationsListStore, permissionsStore } = useStores();

  // A locked transformation must block the connect action regardless of the
  // permission model. The old-model branch below bypasses `canPerformAction`,
  // so the lock would otherwise go unchecked when enforcement is disabled.
  const { isLocked, reason } = useLockedResource(
    LockedResourceType.TRANSFORMATION,
    transformationId,
  );
  if (isLocked) {
    return { isAllowed: false, denialReason: reason };
  }

  return isNewPermissionModelEnforcementEnabled
    ? canPerformAction({
        new: {
          actionType: ActionType.CONNECT_EXISTING,
          resources: [
            { type: ResourceType.TRANSFORMATION, id: transformationId },
            {
              type: ResourceType.DESTINATION,
              id: destinationsListStore.destinations.map((d) => d.id),
            },
          ],
        },
      })
    : constructPermissionResult(
        destinationsListStore.destinations.some((d) =>
          permissionsStore.hasPermission({
            resourceType: 'destination',
            action: 'edit',
            resourceId: d.id,
          }),
        ),
        'You do not have permission to connect this transformation to an existing destination.',
      );
};

// ts-prune-ignore-next
export const canConnectTransformationToDestination = (
  transformationId: string,
  destinationId: string,
) => {
  const { canPerformAction } = useActionGuard();
  return canPerformAction({
    old: { resourceType: 'destination', action: 'edit', resourceId: destinationId },
    new: {
      actionType: ActionType.CONNECT,
      resources: [
        { type: ResourceType.DESTINATION, id: destinationId },
        { type: ResourceType.TRANSFORMATION, id: transformationId },
      ],
    },
  });
};

export const canViewTransformationLiveEvents = (transformationId: string) => {
  const { canPerformAction } = useActionGuard();
  const { transformationsListStore } = useStores();
  const transformation = transformationsListStore.getById(transformationId);

  if (!transformation) {
    return constructPermissionResult(false, 'Transformation not found');
  }

  if (!transformation.destinations || transformation.destinations.length === 0) {
    return constructPermissionResult(false, 'Transformation is not being used by any destination');
  }

  const allDisabled = transformation.destinations.every((dest) => !dest?.enabled);
  if (allDisabled) {
    return constructPermissionResult(false, 'All connected destinations are disabled');
  }

  const allDeviceMode = transformation.destinations.every((elem) =>
    areAllSourcesInDeviceMode(elem?.config, elem?.sources),
  );
  if (allDeviceMode) {
    return constructPermissionResult(
      false,
      'All connected destinations are being used only in device mode',
    );
  }

  return canPerformAction({
    old: { resourceType: 'workspace', action: 'pii' },
    new: {
      actionType: ActionType.PII_LIVE_EVENTS,
      resource: { type: ResourceType.TRANSFORMATION, id: transformationId },
    },
  });
};

export const canDisconnectTransformationFromDestination = (
  transformationId: string,
  destinationId: string,
) => {
  const { canPerformAction } = useActionGuard();
  return canPerformAction({
    old: { resourceType: 'destination', action: 'edit', resourceId: destinationId },
    new: {
      actionType: ActionType.DISCONNECT,
      resources: [
        { type: ResourceType.DESTINATION, id: destinationId },
        { type: ResourceType.TRANSFORMATION, id: transformationId },
      ],
    },
  });
};

export const canModifyTransformationToDestinationConnection = (
  destination: IDestinationStore,
  transformationId: string,
) => {
  const existingTransformationId = destination.transformations[0]?.id;

  const connectNewTransformationPermission = canConnectTransformationToDestination(
    transformationId,
    destination.id,
  );

  const canDisconnectExistingTransformation = canDisconnectTransformationFromDestination(
    existingTransformationId ?? '',
    destination.id,
  ).isAllowed;

  const disconnectExistingTransformationPermission = constructPermissionResult(
    existingTransformationId ? canDisconnectExistingTransformation : true,
    'You do not have permission to disconnect the existing transformation from the destination.',
  );

  if (existingTransformationId === transformationId) {
    return constructPermissionResult(
      connectNewTransformationPermission.isAllowed,
      'You do not have permission to modify this connection.',
    );
  }

  return {
    isAllowed:
      disconnectExistingTransformationPermission.isAllowed &&
      connectNewTransformationPermission.isAllowed,
    denialReason: getMessageList([
      disconnectExistingTransformationPermission.denialReason,
      connectNewTransformationPermission.denialReason,
    ]),
  };
};

export const canConnectDestinationToExistingTransformations = (destinationId: string) => {
  const { canPerformAction } = useActionGuard();
  const { transformationsListStore } = useStores();

  return canPerformAction({
    old: { resourceType: 'destination', action: 'edit', resourceId: destinationId },
    new: {
      actionType: ActionType.CONNECT_EXISTING,
      resources: [
        { type: ResourceType.DESTINATION, id: destinationId },
        {
          type: ResourceType.TRANSFORMATION,
          id: transformationsListStore.transformations.map((d) => d.id),
        },
      ],
    },
  });
};

export const canEditTransformationLibrary = () => {
  const { canPerformAction } = useActionGuard();

  return canPerformAction({
    old: { resourceType: 'transformation', action: 'edit' },
    new: {
      actionType: ActionType.EDIT,
      resource: { type: ResourceType.TRANSFORMATION_LIBRARY },
    },
  });
};

export const canCreateTransformationLibrary = () =>
  constructPermissionResult(
    canEditTransformationLibrary().isAllowed,
    'You do not have permission to create a transformation library.',
  );

export const canDeleteTransformationLibrary = () =>
  constructPermissionResult(
    canEditTransformationLibrary().isAllowed,
    'You do not have permission to delete this transformation library.',
  );

export const canViewTransformationFailureSample = (transformationId: string) => {
  const { canPerformAction } = useActionGuard();

  return canPerformAction({
    old: { resourceType: 'workspace', action: 'pii' },
    new: {
      actionType: ActionType.PII_FAILURE_SAMPLES,
      resource: { type: ResourceType.TRANSFORMATION, id: transformationId },
    },
  });
};
